import React, { useState, useRef, useImperativeHandle, forwardRef } from 'react';
import './FileUpload.css';

const FileUpload = forwardRef(({ onFileAttach, onError }, ref) => {
  const fileInputRef = useRef(null);

  // Поддерживаемые типы файлов
  const supportedTypes = {
    // Аудио - расширенная поддержка
    'audio/mpeg': 'MP3',
    'audio/mp3': 'MP3',
    'audio/wav': 'WAV',
    'audio/wave': 'WAV',
    'audio/x-wav': 'WAV',
    'audio/mp4': 'M4A',
    'audio/m4a': 'M4A',
    'audio/x-m4a': 'M4A',
    'audio/aac': 'AAC',
    'audio/x-aac': 'AAC',
    'audio/flac': 'FLAC',
    'audio/x-flac': 'FLAC',
    'audio/ogg': 'OGG',
    'audio/vorbis': 'OGG',
    'audio/x-ms-wma': 'WMA',
    'audio/wma': 'WMA',
    'audio/opus': 'OPUS',
    'audio/webm': 'WebM Audio',
    'audio/3gpp': '3GP',
    'audio/amr': 'AMR',
    'audio/x-aiff': 'AIFF',
    'audio/aiff': 'AIFF',
    'audio/x-au': 'AU',
    'audio/basic': 'AU',
    
    // Видео - расширенная поддержка
    'video/mp4': 'MP4',
    'video/avi': 'AVI',
    'video/x-msvideo': 'AVI',
    'video/quicktime': 'MOV',
    'video/x-ms-wmv': 'WMV',
    'video/x-flv': 'FLV',
    'video/webm': 'WebM',
    'video/ogg': 'OGV',
    'video/x-matroska': 'MKV',
    'video/mpeg': 'MPEG',
    'video/3gpp': '3GP',
    'video/x-ms-asf': 'ASF',
    
    // Документы
    'application/pdf': 'PDF',
    'application/msword': 'DOC',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
    'text/plain': 'TXT',
    'text/rtf': 'RTF',
    'application/rtf': 'RTF',
    'application/vnd.oasis.opendocument.text': 'ODT'
  };

  const openFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelect = (selectedFiles) => {
    const files = Array.from(selectedFiles);
    const validFiles = [];

    for (const file of files) {
      // Получаем расширение файла
      const fileExtension = file.name.split('.').pop().toLowerCase();
      
      // Проверяем тип файла (сначала по MIME, потом по расширению)
      const isSupportedByMime = supportedTypes[file.type];
      const isSupportedByExtension = getSupportedTypeByExtension(fileExtension);
      
      if (!isSupportedByMime && !isSupportedByExtension) {
        console.log(`Файл ${file.name}: MIME=${file.type}, Extension=${fileExtension}`);
        onError(`Неподдерживаемый тип файла: ${file.name}. Поддерживаются: аудио (MP3, M4A, WAV, FLAC, OGG, AAC, WMA, OPUS), видео (MP4, AVI, MOV, WebM), документы (PDF, DOC, DOCX, TXT, RTF)`);
        continue;
      }

      // Проверяем размер файла (500MB максимум)
      if (file.size > 500 * 1024 * 1024) {
        onError(`Файл слишком большой: ${file.name}. Максимальный размер: 500MB`);
        continue;
      }

      // Определяем категорию файла
      const category = getFileCategoryByExtension(fileExtension) || getFileCategory(file.type);

      validFiles.push({
        file,
        id: Date.now() + Math.random(),
        name: file.name,
        size: file.size,
        type: file.type,
        extension: fileExtension,
        category: category
      });
    }

    if (validFiles.length > 0) {
      // Просто передаем наверх новые валидные файлы
      onFileAttach(validFiles);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files);
    }
    // Очищаем input для возможности повторного выбора того же файла
    e.target.value = '';
  };

  const removeFile = (fileId) => {
    const newAttachedFiles = attachedFiles.filter(f => f.id !== fileId);
    onFileRemove(newAttachedFiles);
  };

  const getFileCategory = (mimeType) => {
    const safeType = mimeType || '';
    if (safeType.startsWith('audio/')) return 'audio';
    if (safeType.startsWith('video/')) return 'video';
    return 'document';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Байт';
    const k = 1024;
    const sizes = ['Байт', 'КБ', 'МБ', 'ГБ'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (mimeType) => {
    const safeType = mimeType || '';
    if (safeType.startsWith('audio/')) return '🎵';
    if (safeType.startsWith('video/')) return '🎬';
    if (safeType.includes('pdf')) return '📄';
    if (safeType.includes('word')) return '📝';
    if (safeType.includes('text')) return '📄';
    return '📎';
  };

  // Функция для проверки поддержки по расширению файла
  const getSupportedTypeByExtension = (extension) => {
    const extensionMap = {
      // Аудио
      'mp3': 'MP3',
      'm4a': 'M4A',
      'wav': 'WAV',
      'flac': 'FLAC',
      'ogg': 'OGG',
      'aac': 'AAC',
      'wma': 'WMA',
      'opus': 'OPUS',
      '3gp': '3GP',
      'amr': 'AMR',
      'aiff': 'AIFF',
      'au': 'AU',
      
      // Видео
      'mp4': 'MP4',
      'avi': 'AVI',
      'mov': 'MOV',
      'wmv': 'WMV',
      'flv': 'FLV',
      'webm': 'WebM',
      'ogv': 'OGV',
      'mkv': 'MKV',
      'mpeg': 'MPEG',
      'mpg': 'MPEG',
      
      // Документы
      'pdf': 'PDF',
      'doc': 'DOC',
      'docx': 'DOCX',
      'txt': 'TXT',
      'rtf': 'RTF',
      'odt': 'ODT'
    };
    
    return extensionMap[extension];
  };

  // Функция для определения категории по расширению
  const getFileCategoryByExtension = (extension) => {
    const audioExtensions = ['mp3', 'm4a', 'wav', 'flac', 'ogg', 'aac', 'wma', 'opus', '3gp', 'amr', 'aiff', 'au'];
    const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'ogv', 'mkv', 'mpeg', 'mpg'];
    
    if (audioExtensions.includes(extension)) return 'audio';
    if (videoExtensions.includes(extension)) return 'video';
    return 'document';
  };

  // Экспортируем функции для внешнего управления
  useImperativeHandle(ref, () => ({
    clearAttachedFiles: () => {
      // Это нужно, если мы захотим очистить файлы извне
    },
    // Экспортируем вспомогательные функции, чтобы ChatInput мог их использовать
    formatFileSize,
    getFileIcon
  }));

  return (
    <>
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*,video/*,.pdf,.doc,.docx,.txt,.rtf,.odt,.m4a,.mp3,.wav,.flac,.ogg,.aac,.wma,.opus,.3gp,.amr,.aiff,.au,.mp4,.avi,.mov,.wmv,.flv,.webm,.ogv,.mkv,.mpeg,.mpg"
          onChange={handleFileInputChange}
          multiple
          style={{ display: 'none' }}
        />
        <button 
          type="button" 
          className="attach-file-btn"
          onClick={openFileDialog}
          title="Прикрепить файлы"
        >
          📎
        </button>
    </>
  );
});

export default FileUpload; 